import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useState } from "react";
import { useInvoiceToPending } from "./useInvoiceToPending";
import { useUpdatePaymentIntent } from "./useUpdatePaymentIntent";
import { uiNotification } from "../../../../../../../services/UINotificationService";
import { useInvoiceToDraft } from "./useInvoiceToDraft";
import { useInvoice } from "../../../../hooks/invoice/useInvoice";
import { dispatch } from "../../../../../../../store/store";
import { checkoutInvoice } from "../../../../../../../store/checkoutInvoice";

let invoiceDetailInterval = null;

export const useConfirmPayment = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { refetchInvoice } = useInvoice();
  const { mutateAsync: setInvoiceToPending } = useInvoiceToPending();
  const { mutateAsync: updatePaymentIntent } = useUpdatePaymentIntent();
  const { mutateAsync: setInvoiceToDraft } = useInvoiceToDraft();

  const [isLoading, setIsLoading] = useState(false);

  const confirmPayment = async (dto) => {
    const completePayment = async () => {
      await refetchInvoice();
      invoiceDetailInterval = setInterval(() => {
        refetchInvoice().then((response) => {
          if (response?.data?.status !== "pending") {
            clearInterval(invoiceDetailInterval);
            uiNotification.success("Payment done successfully");
            dispatch(checkoutInvoice.actions.paymentViewClose());
          }
        });
      }, 2000);
    };

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    await setInvoiceToPending();

    const { error: submitError } = await elements.submit();
    if (submitError) {
      uiNotification.error(submitError.message);
      await setInvoiceToDraft();
      setIsLoading(false);
      return Promise.reject(submitError);
    }

    const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
      elements,
    });
    if (pmError) {
      uiNotification.error(pmError.message);
      await setInvoiceToDraft();
      setIsLoading(false);
      return Promise.reject(pmError);
    }

    const isCreditCard =
      paymentMethod.type !== "card" || paymentMethod.card?.funding === "credit";
    const amountSurcharge = isCreditCard ? dto.amount_surcharge : 0;

    const updateResult = await updatePaymentIntent({
      paymentIntentId: dto.paymentIntentId,
      saveCard: dto.saveCard,
      amount_surcharge: amountSurcharge,
    });

    if (updateResult?.data?.data?.surcharge_removed) {
      uiNotification.info(
        "Surcharge fees were removed based on your payment method. Your total has been updated.",
      );
    }

    if (dto.surcharge_capped) {
      uiNotification.info(
        "Surcharge fees were adjusted based on your payment method and processing rates. Your total has been updated",
      );
    }

    const result = await stripe.confirmPayment({
      clientSecret: dto.clientSecret,
      confirmParams: {
        payment_method: paymentMethod.id,
      },
      redirect: "if_required",
    });

    if (result.error) {
      uiNotification.error(result.error.message);
      await setInvoiceToDraft();
      setIsLoading(false);
      return Promise.reject(result.error);
    } else {
      completePayment();
    }
  };

  return { confirmPayment, isLoading };
};
